import { useAppSelector, useAppTranslation } from "@/utils";
import { Button, Col, Row, useMessagedAsyncFn } from "@payping/web-components";
import { DashboardRoutes } from "@/router";
import { backButtonId, closeId, continueId } from "@/assets/googleTrackIds";
import { Link, useParams } from "react-router-dom";
import { CreateSamanStepEnum, SamanConfiguration, SamanCreateFormType } from "@/pages/DigitalAccount/Saman/Create";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { SamanRowType } from "@/pages/DigitalAccount/Saman/Create/SecondStep";
import React from "react";
import { SamanApis } from "@payping/api";

interface Props {
  formStepOne: FormInstance<SamanConfiguration>;
  formStepTwo: FormInstance<SamanRowType>;
  formStepThree: FormInstance<SamanCreateFormType>;
  setOpenFinalDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  loadingStepTwo: boolean;
  loadingDelete: boolean;
  requestTitle: string;
  requestDescription: string;
  prev: () => void;
  next: () => void;
}

const AnchorSamanButtons: React.FC<Props> = ({
  formStepOne,
  formStepTwo,
  loadingDelete,
  prev,
  formStepThree,
  setOpenFinalDrawer,
  next,
  loadingStepTwo,
  requestTitle,
  requestDescription,
}) => {
  const { t } = useAppTranslation();
  const { step } = useParams<{ step: string }>() as { step: string };
  const stepNumber = parseInt(step, 10) as CreateSamanStepEnum;
  const paymentDetailsModels = useAppSelector(
    (state) => state.digitalAccount.saman.requestList?.paymentDetailsModels,
  );
  const { overviewDetail } = useAppSelector((state) => state.digitalAccount.saman);

  const requestError = paymentDetailsModels?.map((item) => item.errors).filter((errors) => errors);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async () => {
      await SamanApis.submitPaymentWithRequestModelWithCancellationTokenPost({ id: overviewDetail.id });
      next();
      setOpenFinalDrawer(true);
    },
    [overviewDetail],
    undefined,
    { useProgress: true },
  );

  const ButtonElements = () => {
    switch (stepNumber) {
      case CreateSamanStepEnum.Configuration:
        return (
          <Row gutter={16} className="tw-mt-4">
            <Col flex={1}>
              <Button
                type={"primary"}
                block
                disabled={!(requestTitle?.length >= 5 && requestDescription)}
                onClick={() => {
                  formStepOne.submit();
                  next();
                }}
                data-google-track={continueId}
              >
                {t("next")}
              </Button>
            </Col>
            <Col flex={1}>
              <Link to={DashboardRoutes.digitalAccount.yourAccount.url}>
                <Button block data-google-track={closeId}>
                  {t("cancel")}
                </Button>
              </Link>
            </Col>
          </Row>
        );
      case CreateSamanStepEnum.CreateTransfer:
        return (
          <Row gutter={16} className="tw-mt-4">
            <Col flex={2}>
              <Button
                loading={loadingStepTwo}
                disabled={!(paymentDetailsModels && paymentDetailsModels.length > 0)}
                onClick={() => formStepTwo.submit()}
                type={"primary"}
                block
                data-google-track={continueId}
              >
                {requestError?.length || loadingDelete ? t("deleteAllErrors&continue") : t("next")}
              </Button>
            </Col>
            <Col flex={1}>
              <Button onClick={() => prev()} block data-google-track={backButtonId}>
                {t("return")}
              </Button>
            </Col>
          </Row>
        );
      case CreateSamanStepEnum.Verification:
        return (
          <Row gutter={16} className="tw-mt-4">
            <Col flex={2}>
              <Button
                type={"primary"}
                block
                onClick={async () => {
                  formStepThree.submit();
                  await onSubmit();
                }}
                loading={loadingSubmit}
                data-google-track={continueId}
              >
                {t("ok&submit")}
              </Button>
            </Col>
            <Col flex={1}>
              <Button onClick={() => prev()} block data-google-track={backButtonId}>
                {t("return")}
              </Button>
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  return <ButtonElements />;
};

export default AnchorSamanButtons;
